import { pricingAPI } from '../services/PaymentPricingApi';
import { paymentsAPI } from '../services/PaymentApi';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { X, Plus, Pencil, Trash2, Check, DollarSign, CalendarRange, Loader2, ArrowDownToLine } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';


/**
 * Attach prices to anything.
 *
 * One modal serves every priced entity — a session, a series, a programme, a
 * tournament, a court, a membership — because `prices` is polymorphic and the
 * only thing that changes between them is the owner pair. A per-entity pricing
 * screen would be the same four fields copied five times.
 *
 * Two things about the domain the UI has to be honest about:
 *
 *  - A price is withdrawn, never deleted. Order lines point at the price they
 *    were bought at, so removing the row would take the receipt with it. The
 *    button therefore says Withdraw, and withdrawn rows stay reachable.
 *
 *  - Levels do not merge. An occurrence's price comes from the first level that
 *    answers — event, then series, then programme — so attaching a price here
 *    replaces what was inherited rather than adding to it. That is a surprise
 *    worth spelling out on screen, not in a doc.
 */

const PRICING_MODELS = [
  { value: 'one_off',     label: 'One-off',      hint: 'Charged once' },
  { value: 'per_session', label: 'Per session',  hint: 'Charged each time they play' },
  { value: 'monthly',     label: 'Monthly',      hint: 'Recurring each month' },
  { value: 'full_term',   label: 'Full term',    hint: 'One payment for the whole term' },
];

const AUDIENCES = [
  { value: 'everyone', label: 'Everyone' },
  { value: 'member',   label: 'Members only' },
  { value: 'guest',    label: 'Guests only' },
];

// Labels for the owner kinds the modal can be pointed at, used in the header
// and in the inheritance notice.
const OWNER_LABELS = {
  event:        'this session',
  event_series: 'the whole series',
  program:      'this programme',
  tournament:   'this tournament',
  court:        'this court',
  membership:   'this membership',
};

const emptyDraft = () => ({
  label: '',
  amount: '',
  pricingModel: 'one_off',
  audience: 'everyone',
  validFrom: '',
  validUntil: '',
  // Off means no fee at all, which is null on the wire rather than a zero
  // nobody applies.
  bookingFeeOn: false,
  bookingFeeType: 'percent',
  bookingFeeValue: '',
  // '' means "follow what we agreed with Kynaix". A price tag only names a
  // mode when it wants to differ, which is the exception.
  platformFeeMode: '',
  // Placeholder. The toggle is here so the shape of the record is visible;
  // nothing reads it yet and it is not sent to the server.
  allowMembershipPricing: false,
});

// Enough to render a currency without a table of every ISO code — anything
// unlisted falls back to showing the code itself, which is honest.
const CURRENCY_SYMBOLS = {
  USD: '$', CAD: '$', AUD: '$', NZD: '$', SGD: '$',
  EUR: '€', GBP: '£', INR: '₹', JPY: '¥', CHF: 'CHF',
  AED: 'AED', MXN: '$', BRL: 'R$', ZAR: 'R',
};
const symbolFor = (code) => CURRENCY_SYMBOLS[code] || code || '$';

export const formatMoney = (amount, currency = 'USD') => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: n % 1 ? 2 : 0 }).format(n);
  } catch {
    // An unknown currency code should not take the row down with it.
    return `${currency} ${n.toFixed(2)}`;
  }
};

/**
 * The pricing UI itself, with no chrome of its own.
 *
 * Kept separate from the modal so a caller that is already inside one can drill
 * into pricing rather than stack another overlay on top: three layers deep,
 * nothing on screen tells you which Cancel belongs to which thing.
 */
export const PricingPanel = ({
  academyId,
  ownerType,
  ownerId,
  // What is being priced, for the header — "Tuesday Open Play".
  entityName = '',
  // Offered as a second target when the caller has one: a recurring session can
  // be priced per night or once for the series.
  seriesId = null,
  // Where an event's price would otherwise come from, so the modal can say what
  // attaching one will override. { level, prices } from pricingAPI.forEvent.
  inherited = null,
  // Reported upward so an embedding modal can title itself, rather than
  // growing a header of its own inside the panel.
  onScopeLabelChange,
  // Fired whenever the list changes, so a caller showing a summary of these
  // prices does not go stale the moment one is added here.
  onPricesChange,
}) => {
  const { theme } = useTheme();

  // Which entity the prices being shown belong to. Starts at the caller's own
  // owner and can be switched to the series when there is one.
  const [scope, setScope] = useState(ownerType);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showWithdrawn, setShowWithdrawn] = useState(false);
  // null = closed, 'new' = adding, otherwise the id being edited.
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [confirmWithdraw, setConfirmWithdraw] = useState(null);
  // Set just before a reload that follows a write, so the caller can tell
  // "here is the list" from "the list just changed" and refresh accordingly.
  const wroteRef = useRef(false);
  // Names already in use here, and what this academy charges in. Fetched once:
  // the form needs the currency before any price exists to read it from.
  const [tags, setTags] = useState([]);
  const [currency, setCurrency] = useState('USD');

  // `ownerId` is whatever the caller pointed at, which may itself be a series;
  // `seriesId` only comes into play when the caller offered the series as a
  // second target and the user switched to that tab. Keying purely on
  // scope === 'event_series' wrote owner_id as the string "null" for a caller
  // that passed the series as its own owner and no seriesId.
  const activeOwnerId = (scope === ownerType) ? ownerId : seriesId;

  const load = useCallback(async () => {
    if (!academyId || !activeOwnerId) return;
    setLoading(true);
    try {
      // Always fetched with inactive included: the list is the management view,
      // and hiding withdrawn rows here would hide them from the person who has
      // to un-withdraw them. The toggle filters on the client.
      const rows = await pricingAPI.list(academyId, scope, activeOwnerId, { includeInactive: true });
      const list = Array.isArray(rows) ? rows : [];
      setPrices(list);
      const afterWrite = wroteRef.current;
      wroteRef.current = false;
      if (onPricesChange) onPricesChange(list.filter(p => p.isActive), { afterWrite });
    } catch (err) {
      toast.error(err.message || 'Could not load prices');
      setPrices([]);
    } finally {
      setLoading(false);
    }
  }, [academyId, scope, activeOwnerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setScope(ownerType);
    setEditing(null);
    setConfirmWithdraw(null);
  }, [ownerType]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!academyId) return;
    let cancelled = false;
    pricingAPI.tags(academyId)
      .then(res => {
        if (cancelled) return;
        setTags(Array.isArray(res?.tags) ? res.tags : []);
        if (res?.currency) setCurrency(res.currency);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [academyId]);

  /**
   * The academy's agreed platform fee, so the form can show what a price
   * actually nets rather than only offering a policy dropdown.
   *
   * Read-only and failure-tolerant: a host who cannot load their terms should
   * still be able to set a price. The server decides the real fee at checkout
   * regardless of anything shown here.
   */
  const [feeTerms, setFeeTerms] = useState(null);
  useEffect(() => {
    if (!academyId) return;
    let cancelled = false;
    paymentsAPI.getFeeAgreement('academy', academyId)
      .then(res => {
        if (cancelled) return;
        const t = res?.state === 'signed' ? res.agreement : res?.terms;
        if (!t) return;
        setFeeTerms({
          signed: res.state === 'signed',
          percent: Number(t.percent),
          fixed: Number(t.fixed),
          currency: t.currency,
          defaultFeeMode: t.defaultFeeMode || 'customer',
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [academyId]);

  const visible = showWithdrawn ? prices : prices.filter(p => p.isActive);
  const withdrawnCount = prices.filter(p => !p.isActive).length;

  const startAdd = () => {
    setDraft(emptyDraft());
    setEditing('new');
  };

  const startEdit = (price) => {
    setDraft({
      label: price.label,
      amount: String(price.amount),
      currency: price.currency || 'USD',
      pricingModel: price.pricingModel,
      audience: price.audience,
      validFrom: price.validFrom || '',
      validUntil: price.validUntil || '',
      platformFeeMode: price.platformFeeMode || '',
      bookingFeeOn: Boolean(price.bookingFee),
      bookingFeeType: price.bookingFee?.type || 'percent',
      bookingFeeValue: price.bookingFee ? String(price.bookingFee.value) : '',
      allowMembershipPricing: false,
    });
    setEditing(price.id);
  };

  const validate = () => {
    if (!draft.label.trim()) return 'Give the price a label buyers will recognise.';
    const amount = Number(draft.amount);
    if (!Number.isFinite(amount) || amount < 0) return 'Amount must be zero or more.';
    if (draft.validFrom && draft.validUntil && draft.validUntil < draft.validFrom) {
      return 'The window ends before it starts.';
    }
    if (draft.bookingFeeOn) {
      const fee = Number(draft.bookingFeeValue);
      if (!Number.isFinite(fee) || fee < 0) return 'Booking fee must be zero or more.';
      if (draft.bookingFeeType === 'percent' && fee > 100) return 'A percentage booking fee cannot exceed 100.';
    }
    return null;
  };

  const save = async () => {
    const problem = validate();
    if (problem) { toast.error(problem); return; }

    setSaving(true);
    try {
      const payload = {
        label: draft.label.trim(),
        amount: Number(draft.amount),
        // Currency is not sent: the server sets it from the academy's country.
        pricingModel: draft.pricingModel,
        bookingFee: draft.bookingFeeOn
          ? { type: draft.bookingFeeType, value: Number(draft.bookingFeeValue) }
          : null,
        // Null clears the override and falls back to the academy's signed
        // choice — deliberately not the same thing as choosing 'customer'.
        platformFeeMode: draft.platformFeeMode || null,
        audience: draft.audience,
        // Empty means no bound, which is null rather than an omitted field —
        // clearing a window has to reach the server as a clear.
        validFrom: draft.validFrom || null,
        validUntil: draft.validUntil || null,
      };

      // Guarded because load() bails on a missing owner but save() did not, so
      // a bad owner surfaced as a successful write to nothing.
      if (!activeOwnerId) throw new Error('Nothing to attach this price to.');

      if (editing === 'new') {
        // Appended rather than inserted, so adding a price never reshuffles the
        // order buyers already saw.
        await pricingAPI.create(academyId, {
          ...payload,
          ownerType: scope,
          ownerId: String(activeOwnerId),
          sortOrder: prices.length,
        });
        toast.success('Price attached');
      } else {
        await pricingAPI.update(academyId, editing, payload);
        toast.success('Price updated');
      }
      setEditing(null);
      wroteRef.current = true;
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not save the price');
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async (price) => {
    setSaving(true);
    try {
      await pricingAPI.deactivate(academyId, price.id);
      toast.success('Price withdrawn');
      setConfirmWithdraw(null);
      wroteRef.current = true;
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not withdraw the price');
    } finally {
      setSaving(false);
    }
  };

  const restore = async (price) => {
    setSaving(true);
    try {
      await pricingAPI.update(academyId, price.id, { isActive: true });
      toast.success('Price restored');
      wroteRef.current = true;
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not restore the price');
    } finally {
      setSaving(false);
    }
  };

  const input = `w-full px-3 py-2 rounded-lg border ${theme.border} ${theme.text} text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`;
  const scopeLabel = OWNER_LABELS[scope] || 'this item';

  // Which way the resolver would look from here. A price is answered by the
  // most specific level that has one, so where the current answer sits relative
  // to the tab being edited decides what the host needs warning about.
  const rank = { event: 0, event_series: 1, program: 2 };
  const answeredAt = inherited?.prices?.length ? inherited.level : null;
  /* ⚠️ AN ATTACHED TAG IS NOT A PRIVATE ONE.
   *
   * `createPrice` stores every tag against the SELLER — "an academy holds Adult
   * drop-in once and points every night at it" — and `price_id` on the offering
   * is just a pointer to it. So a tag on screen here may be shared with any
   * number of other offerings, and the pencil rewrites it for all of them.
   *
   * That is fine when the host means it, and a trap when they do not: a
   * migration pointed every unpriced offering at its academy's "Free" row, and
   * with Add hidden behind "there is already a live price" the only way to
   * price a court was to edit Free — repricing everything else pointing at it.
   *
   * So Add is offered whenever the tag is one the academy holds rather than one
   * this offering had to itself, which — given the rule above — is always.
   * Adding moves this offering's pointer and leaves the shared row alone. */
  const ownsIt = (p) => p.ownerType === scope && String(p.ownerId) === String(activeOwnerId);
  const hasOwn = prices.some(p => p.isActive && ownsIt(p));

  // Nothing here, and the answer comes from further up: say where from, because
  // adding a price replaces it rather than adding to it.
  const inheritsFrom = (!hasOwn && answeredAt && rank[answeredAt] > rank[scope]) ? answeredAt : null;

  // The answer comes from further down instead — a single night priced on its
  // own ignores whatever the series says. Setting a price here is still useful
  // for the other nights, so this is a caveat rather than a warning.
  const overriddenBy = (answeredAt && rank[answeredAt] < rank[scope]) ? answeredAt : null;

  useEffect(() => {
    if (onScopeLabelChange) onScopeLabelChange(scopeLabel);
  }, [scopeLabel, onScopeLabelChange]);

  return (
        <div className="space-y-4">
          {/* A recurring session can be priced per night or once for every
              night, and which one the host means cannot be guessed. */}
          {seriesId && (
            <div className={`inline-flex rounded-lg border ${theme.border} p-0.5`}>
              {[
                { value: ownerType,      label: 'This session' },
                { value: 'event_series', label: 'Whole series' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setEditing(null); setScope(opt.value); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    scope === opt.value ? 'bg-blue-600 text-white' : `${theme.textSecondary} ${theme.cardHover}`
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {inheritsFrom && (
            <div className={`rounded-lg border ${theme.border} bg-amber-50 dark:bg-amber-900/20 p-3`}>
              <div className="flex items-start gap-2">
                <ArrowDownToLine className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className={`font-medium ${theme.text}`}>
                    Currently inherited from {OWNER_LABELS[inheritsFrom] || inheritsFrom}
                  </p>
                  <p className={`${theme.textMuted} mt-0.5`}>
                    {inherited.prices.map(p => `${p.label} ${formatMoney(p.amount, p.currency)}`).join(' · ')}
                  </p>
                  <p className={`${theme.textMuted} mt-1`}>
                    Adding a price here replaces those for {scopeLabel} — the two do not add together.
                  </p>
                </div>
              </div>
            </div>
          )}

          {overriddenBy && (
            <div className={`rounded-lg border ${theme.border} ${theme.backgroundSecondary} p-3`}>
              <div className="flex items-start gap-2">
                <ArrowDownToLine className={`w-4 h-4 ${theme.textMuted} flex-shrink-0 mt-0.5 rotate-180`} />
                <p className={`text-sm ${theme.textMuted} first-letter:uppercase`}>
                  {OWNER_LABELS[overriddenBy] || overriddenBy} has its own price
                  {' '}({inherited.prices.map(p => formatMoney(p.amount, p.currency)).join(' · ')}), which wins over
                  anything set here. What you set for {scopeLabel} applies to the rest.
                </p>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className={`w-6 h-6 animate-spin ${theme.textMuted}`} />
            </div>
          ) : (
            <div className="space-y-2">
              {visible.length === 0 && editing !== 'new' && (
                <div className={`text-center py-8 rounded-lg border border-dashed ${theme.border}`}>
                  <DollarSign className={`w-8 h-8 mx-auto mb-2 ${theme.textMuted}`} />
                  <p className={`text-sm ${theme.textMuted}`}>
                    {inheritsFrom
                      ? `No price of its own — ${scopeLabel} uses the inherited one.`
                      : overriddenBy
                        // "Free unless you attach one" would be a lie here: the
                        // more specific level is already charging.
                        ? `Nothing set for ${scopeLabel} — ${OWNER_LABELS[overriddenBy] || overriddenBy} keeps its own price either way.`
                        : 'No prices yet. Free unless you attach one.'}
                  </p>
                </div>
              )}

              {visible.map(price => (
                editing === price.id ? (
                  <PriceForm
                    key={price.id}
                    draft={draft} setDraft={setDraft} theme={theme} input={input}
                    saving={saving} onSave={save} onCancel={() => setEditing(null)}
                    tags={tags} currency={currency} feeTerms={feeTerms}
                  />
                ) : (
                  <div
                    key={price.id}
                    className={`p-3 rounded-lg border ${theme.border} ${price.isActive ? '' : 'opacity-60'}`}
                  >
                   <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-semibold ${theme.text}`}>
                          {formatMoney(price.amount, price.currency)}
                        </span>
                        <span className={`text-sm ${theme.text}`}>{price.label}</span>
                        {!price.isActive && (
                          <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            Withdrawn
                          </span>
                        )}
                      </div>
                      <div className={`text-xs ${theme.textMuted} mt-1 flex items-center gap-2 flex-wrap`}>
                        <span>{PRICING_MODELS.find(m => m.value === price.pricingModel)?.label || price.pricingModel}</span>
                        {price.audience !== 'everyone' && (
                          <span>· {AUDIENCES.find(a => a.value === price.audience)?.label}</span>
                        )}
                        {price.bookingFee && (
                          <span>
                            · +{price.bookingFee.type === 'percent'
                              ? `${price.bookingFee.value}%`
                              : formatMoney(price.bookingFee.value, price.currency)} booking fee
                          </span>
                        )}
                        {(price.validFrom || price.validUntil) && (
                          <span className="inline-flex items-center gap-1">
                            · <CalendarRange className="w-3 h-3" />
                            {price.validFrom || 'any time'} → {price.validUntil || 'no end'}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                        {price.isActive ? (
                          <>
                            <button
                              onClick={() => startEdit(price)}
                              className={`p-1.5 rounded-lg ${theme.cardHover} text-blue-500 hover:text-blue-600`}
                              title="Edit price"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setConfirmWithdraw(price.id)}
                              className={`p-1.5 rounded-lg ${theme.cardHover} text-red-500 hover:text-red-600`}
                              title="Withdraw price"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => restore(price)}
                            disabled={saving}
                            className={`text-xs font-medium text-blue-500 hover:text-blue-600 px-2 py-1`}
                          >
                            Restore
                          </button>
                        )}
                    </div>

                   </div>

                    {/* Confirmed in place. A dialog over a dialog over a form
                        leaves three Cancels on screen and no way to tell which
                        one backs out of what. */}
                    {confirmWithdraw === price.id && (
                      <div className={`mt-3 pt-3 border-t ${theme.border}`}>
                        <p className={`text-sm ${theme.textMuted}`}>
                          Stop offering this price? Anything already bought at it keeps its
                          receipt, and you can restore it later.
                        </p>
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => setConfirmWithdraw(null)}
                            className={`${btn.base} ${btn.sm} ${theme.buttonGhost}`}
                          >
                            Keep it
                          </button>
                          <button
                            onClick={() => withdraw(price)}
                            disabled={saving}
                            className={`${btn.base} ${btn.sm} ${theme.buttonDanger}`}
                          >
                            Withdraw
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              ))}

              {editing === 'new' && (
                <PriceForm
                  draft={draft} setDraft={setDraft} theme={theme} input={input}
                  saving={saving} onSave={save} onCancel={() => setEditing(null)}
                  tags={tags} currency={currency} feeTerms={feeTerms}
                />
              )}
            </div>
          )}

          {/* One live price per entity, so this disappears once there is one.
              Charging differently for members or by the year is a property of
              that price, not a second competing tag. */}
          {editing === null && !hasOwn && (
            <button
              onClick={startAdd}
              className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed ${theme.border} ${theme.textSecondary} ${theme.cardHover} text-sm font-medium`}
            >
              <Plus className="w-4 h-4" />
              Add a price
            </button>
          )}

          {withdrawnCount > 0 && (
            <button
              onClick={() => setShowWithdrawn(v => !v)}
              className={`text-xs ${theme.textMuted} hover:underline`}
            >
              {showWithdrawn ? 'Hide' : 'Show'} {withdrawnCount} withdrawn
            </button>
          )}
        </div>
  );
};

/**
 * The same panel as a standalone dialog, for callers that are not already
 * inside one — the programme page, and anywhere else pricing is reached from a
 * plain screen.
 */
const AttachPriceModal = ({ isOpen, onClose, entityName = '', ...panelProps }) => {
  const { theme } = useTheme();
  const [scopeLabel, setScopeLabel] = useState('');
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className={`${theme.card} rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl`}>
        <div className={`flex items-start justify-between p-5 border-b ${theme.border} sticky top-0 ${theme.card} z-10`}>
          <div className="min-w-0">
            <h3 className={`text-lg font-semibold ${theme.text}`}>Pricing</h3>
            <p className={`text-sm ${theme.textMuted} truncate`}>
              {entityName ? `${entityName} — ` : ''}what people pay for {scopeLabel}
            </p>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg ${theme.cardHover}`}>
            <X className={`w-5 h-5 ${theme.text}`} />
          </button>
        </div>

        <div className="p-5">
          <PricingPanel {...panelProps} entityName={entityName} onScopeLabelChange={setScopeLabel} />
        </div>

        <div className={`p-5 border-t ${theme.border} flex justify-end`}>
          <button onClick={onClose} className={`${btn.base} ${btn.md} ${theme.buttonPrimary}`}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// The add and edit forms are the same eight fields, so they are the same
// component rendered in two places rather than two copies drifting apart.

const FEE_MODE_OPTIONS = [
  { value: 'customer', label: "Add it to the customer's total" },
  { value: 'split_50', label: 'Split it evenly' },
  { value: 'absorb',   label: 'Take it from my share' },
];

const FEE_MODE_LABEL = {
  customer: 'added to the customer\'s total',
  split_50: 'split evenly',
  absorb:   'taken from your share',
};

/**
 * What this price actually nets, at the agreed rate.
 *
 * Recomputed here rather than fetched, because it has to move as the amount is
 * typed. The server remains the authority at checkout — this is a preview, and
 * it says so by never being the number that is charged.
 */
const FeeMath = ({ terms, amount, mode, currency, theme }) => {
  const fmt = (n) => `${currency === 'USD' ? '$' : ''}${Number(n || 0).toFixed(2)}${currency === 'USD' ? '' : ` ${currency}`}`;
  const rate = `${terms.percent}% + ${fmt(terms.fixed)}`;

  if (!Number.isFinite(amount) || amount <= 0) {
    return (
      <p className={`text-xs ${theme.textMuted} mt-3 rounded-lg px-3 py-2.5 bg-black/10 border ${theme.border}`}>
        Kynaix keeps {rate} per online payment.
      </p>
    );
  }

  const fee = Math.round((amount * terms.percent / 100 + Number(terms.fixed)) * 100) / 100;
  const fromCustomer = mode === 'absorb' ? 0 : mode === 'split_50' ? Math.round(fee / 2 * 100) / 100 : fee;
  const fromHost  = Math.round((fee - fromCustomer) * 100) / 100;
  const buyerPays = Math.round((amount + fromCustomer) * 100) / 100;
  const youKeep   = Math.round((amount - fromHost) * 100) / 100;

  /** One invoice row: what it is on the left, what it costs on the right. */
  const Row = ({ label, value, strong = false, muted = false }) => (
    <div className="flex items-baseline justify-between gap-4">
      <span className={`${strong ? `text-sm font-semibold ${theme.text}` : `text-xs ${muted ? theme.textMuted : theme.textSecondary}`}`}>
        {label}
      </span>
      {/* tabular-nums so the decimal points line up down the column — the whole
          point of laying this out as an invoice rather than a sentence. */}
      <span className={`tabular-nums whitespace-nowrap ${strong ? `text-sm font-semibold ${theme.text}` : `text-xs ${muted ? theme.textMuted : theme.textSecondary}`}`}>
        {value}
      </span>
    </div>
  );

  return (
    <div className={`mt-3 rounded-lg px-3 py-2.5 bg-black/10 border ${theme.border} space-y-1.5`}>
      <Row label="Your price" value={fmt(amount)} />
      {fromCustomer > 0 && (
        <Row label={`Kynaix service fee (${rate})`} value={`+ ${fmt(fromCustomer)}`} />
      )}

      <div className={`border-t ${theme.border} pt-1.5`}>
        <Row label="Buyer pays" value={fmt(buyerPays)} strong />
      </div>

      {fromHost > 0 && (
        <Row label={`Kynaix service fee (${rate})`} value={`− ${fmt(fromHost)}`} />
      )}

      <div className={`border-t ${theme.border} pt-1.5`}>
        <Row label="You receive" value={fmt(youKeep)} strong />
      </div>

      <p className={`text-xs italic ${theme.textMuted} pt-0.5`}>
        Card payments only. Cash at the counter is unaffected.
      </p>
    </div>
  );
};

const PriceForm = ({ draft, setDraft, theme, input, saving, onSave, onCancel, tags = [], currency = 'USD', feeTerms = null }) => {
  const set = (patch) => setDraft(prev => ({ ...prev, ...patch }));
  const [showWindow, setShowWindow] = useState(Boolean(draft.validFrom || draft.validUntil));
  const symbol = symbolFor(currency);

  // Picking a tag fills the whole record the way it was last used, not just the
  // name — the point of reusing "Drop-in" is that it already means $15 per
  // session with the same fee, and retyping those is how they drift apart.
  // Matched on the typed name, so the select reflects a tag chosen either way
  // and falls back to "New price tag…" the moment the name is edited.
  const selectedTag = tags.find(
    t => t.label.trim().toLowerCase() === draft.label.trim().toLowerCase()
  );

  const applyTag = (tag) => set({
    label: tag.label,
    amount: String(tag.amount ?? ''),
    pricingModel: tag.pricingModel || 'one_off',
    audience: tag.audience || 'everyone',
    platformFeeMode: tag.platformFeeMode || '',
    bookingFeeOn: Boolean(tag.bookingFee),
    bookingFeeType: tag.bookingFee?.type || 'percent',
    bookingFeeValue: tag.bookingFee ? String(tag.bookingFee.value) : '',
  });

  return (
    <div className={`p-4 rounded-lg border-2 border-blue-500/40 ${theme.backgroundSecondary} space-y-3`}>
      {/* A list, not chips: an academy accumulates price tags forever, and by
          seven of them the chips already wrapped to two rows and pushed the
          form off screen. A select stays one line at any number. */}
      {tags.length > 0 && (
        <div>
          <label className={`block text-xs font-medium ${theme.text} mb-1`}>Reuse a price tag</label>
          <select
            className={input}
            value={selectedTag ? selectedTag.label : ''}
            onChange={e => {
              const tag = tags.find(t => t.label === e.target.value);
              // Choosing the blank option is the deliberate "start fresh"
              // action, so it clears rather than leaving the last tag's values
              // sitting under a name the user is about to replace.
              if (tag) applyTag(tag);
              else set({ label: '', amount: '' });
            }}
          >
            <option value="">New price tag…</option>
            {tags.map(tag => (
              <option key={tag.label} value={tag.label}>
                {tag.label} · {formatMoney(tag.amount, tag.currency)}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <label className={`block text-xs font-medium ${theme.text} mb-1`}>Price tag *</label>
          <input
            className={input}
            value={draft.label}
            onChange={e => set({ label: e.target.value })}
            placeholder="Drop-in, Monthly pass, Member rate"
            maxLength={120}
          />
        </div>
        <div>
          <label className={`block text-xs font-medium ${theme.text} mb-1`}>
            Price * <span className={`font-normal ${theme.textMuted}`}>({currency})</span>
          </label>
          <div className="relative">
            <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${theme.textMuted}`}>{symbol}</span>
            <input
              className={`${input} ${symbol.length > 1 ? 'pl-12' : 'pl-7'}`}
              type="number" min="0" step="0.01" inputMode="decimal"
              value={draft.amount}
              onChange={e => set({ amount: e.target.value })}
              placeholder="0.00"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={`block text-xs font-medium ${theme.text} mb-1`}>Charged</label>
          <select className={input} value={draft.pricingModel} onChange={e => set({ pricingModel: e.target.value })}>
            {PRICING_MODELS.map(m => <option key={m.value} value={m.value}>{m.label} — {m.hint}</option>)}
          </select>
        </div>
        <div>
          <label className={`block text-xs font-medium ${theme.text} mb-1`}>Available to</label>
          <select className={input} value={draft.audience} onChange={e => set({ audience: e.target.value })}>
            {AUDIENCES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
      </div>

      {/* Not wired to anything yet — the toggle is here so the shape of a price
          record is visible while the membership side is designed. It is not
          sent, and nothing reads it. */}
      <label className={`flex items-start gap-2 p-2.5 rounded-lg border ${theme.border} cursor-pointer`}>
        <input
          type="checkbox"
          checked={draft.allowMembershipPricing}
          onChange={e => set({ allowMembershipPricing: e.target.checked })}
          className="w-4 h-4 mt-0.5 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
        />
        <span className="min-w-0">
          <span className={`block text-xs font-medium ${theme.text}`}>
            Allow membership pricing
            <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
              Not active yet
            </span>
          </span>
          <span className={`block text-xs ${theme.textMuted} mt-0.5`}>
            Members will get their own rate for this. Nothing changes until membership pricing ships.
          </span>
        </span>
      </label>

      {/* KYNAIX'S SERVICE FEE — who funds it, for this price only.
          Separate from the venue's own booking fee above and deliberately so:
          that one is the academy's money, this one is Kynaix's, and folding
          them into a single control would make settlement ambiguous the moment
          an academy sets both. Blank means "follow what we agreed", which is
          what almost every price should say. */}
      <div className={`p-2.5 rounded-lg border ${theme.border}`}>
        <span className={`block text-sm font-semibold ${theme.text}`}>Kynaix service fee</span>
        <span className={`block text-xs ${theme.textMuted} mt-1 mb-2.5`}>
          Who funds it on this price tag. Leave as agreed unless this one differs.
        </span>
        <select
          className={input}
          value={draft.platformFeeMode}
          onChange={e => set({ platformFeeMode: e.target.value })}
        >
          {/* The inherited value is spelled out, not left as a black box: a
              host reading "same as our agreement" still does not know what
              they are charging. The matching concrete option is marked too, so
              the effective setting is visible with the list open or shut. */}
          <option value="">
            {feeTerms?.defaultFeeMode
              ? `Same as our agreement — ${FEE_MODE_LABEL[feeTerms.defaultFeeMode]}`
              : 'Same as our agreement'}
          </option>
          {FEE_MODE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
              {feeTerms?.defaultFeeMode === opt.value ? '  (your default)' : ''}
            </option>
          ))}
        </select>

        {/* THE NUMBERS, not just the policy. "Same as our agreement" tells a
            host nothing about what they actually keep — and the fee is the one
            thing on this form that is not their own decision, so it has to be
            legible rather than merely configurable. */}
        {feeTerms?.signed ? (
          <FeeMath
            terms={feeTerms}
            amount={Number(draft.amount)}
            mode={draft.platformFeeMode || feeTerms.defaultFeeMode || 'customer'}
            currency={currency}
            theme={theme}
          />
        ) : (
          <p className={`text-xs ${theme.textMuted} mt-3 rounded-lg px-3 py-2.5 bg-black/10 border ${theme.border}`}>
            No agreement signed yet, so nothing is deducted. Sign it in
            Settings → Payouts before switching on online payments.
          </p>
        )}
      </div>

      {/* The venue's own "online booking fee" control used to be here.
          Taken out now that Kynaix's service fee is shown on this form: two
          separate surcharges on one checkout is a receipt nobody can read.
          The column stays in the schema and the server still honours
          anything already configured — nothing currently is. */}

      {showWindow ? (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={`block text-xs font-medium ${theme.text} mb-1`}>On sale from</label>
            <input className={input} type="date" value={draft.validFrom} onChange={e => set({ validFrom: e.target.value })} />
          </div>
          <div>
            <label className={`block text-xs font-medium ${theme.text} mb-1`}>Until</label>
            <input className={input} type="date" value={draft.validUntil} onChange={e => set({ validUntil: e.target.value })} />
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowWindow(true)}
          className={`text-xs ${theme.textMuted} hover:underline inline-flex items-center gap-1`}
        >
          <CalendarRange className="w-3 h-3" />
          Add an on-sale window (early bird, seasonal)
        </button>
      )}

      <div className="flex gap-2 pt-1">
        <button onClick={onCancel} className={`${btn.base} ${btn.sm} ${theme.buttonGhost} flex-1`}>
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className={`${btn.base} ${btn.sm} ${theme.buttonPrimary} flex-1 flex items-center justify-center gap-1.5`}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Save price
        </button>
      </div>
    </div>
  );
};

export default AttachPriceModal;
